// M8 — Arşiv + readonly view + documents listesi (plan §4.6).

import { z } from "zod";
import { PeriodType } from "./period.js";

export const ArchiveListItemSchema = z.object({
  id: z.string().uuid(),
  clientName: z.string(),
  clientSector: z.string().nullable(),
  frameworkName: z.string(),
  frameworkVersion: z.string(),
  reportingYear: z.number().int(),
  periodType: PeriodType,
  assignedToName: z.string(),
  completedAt: z.string(),
  responseCount: z.number().int().nonnegative(),
  // Geçmiş Rapor Aktarımı (G8/4A): proje aktif bir rapor aktarımından doğduysa true.
  // OPSİYONEL — eski tüketiciler ve dar DTO key-set testleri etkilenmesin diye
  // additive eklendi (backend her zaman doldurur).
  hasImport: z.boolean().optional(),
});
export type ArchiveListItem = z.infer<typeof ArchiveListItemSchema>;

// Multi-select filter — karar #1 (array encoding + IN(...))
export const ArchiveListQuerySchema = z
  .object({
    sectors: z.union([z.string(), z.array(z.string())]).optional(),
    years: z.union([z.string(), z.array(z.string())]).optional(),
    clientIds: z.union([z.string(), z.array(z.string())]).optional(),
    // Raporlama dönemi filtresi — yıl çoklu seçim (years), dönem TEKİL:
    // arayüzde "Dönem (tümü)" tek seçimli bir daraltmadır (spec 2C).
    periodType: PeriodType.optional(),
    search: z.string().max(200).optional(),
    // Geçmiş Rapor Aktarımı (G8/4A): "Kaynak" daraltması — aktarımdan doğan vs. elle
    // oluşturulan proje. `ProjectsListQuerySchema.source` ile aynı enum.
    source: z.enum(["import", "manual"]).optional(),
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(25),
  })
  .transform((v) => ({
    sectors: toArray(v.sectors),
    years: toArray(v.years)
      ?.map((y) => parseInt(y, 10))
      .filter((n) => !isNaN(n)),
    clientIds: toArray(v.clientIds),
    periodType: v.periodType,
    search: v.search,
    source: v.source,
    page: v.page,
    limit: v.limit,
  }));
export type ArchiveListQuery = z.infer<typeof ArchiveListQuerySchema>;

function toArray(v: string | string[] | undefined): string[] | undefined {
  if (v === undefined) return undefined;
  if (Array.isArray(v)) return v.filter((x) => x.length > 0);
  if (v.length === 0) return undefined;
  // CSV veya JSON array parse
  if (v.startsWith("[")) {
    try {
      const arr = JSON.parse(v) as unknown;
      if (Array.isArray(arr)) return arr.map(String).filter((x) => x.length > 0);
    } catch {
      // CSV fallback
    }
  }
  return v
    .split(",")
    .map((s) => s.trim())
    .filter((x) => x.length > 0);
}

export const ArchiveListResponseSchema = z.object({
  items: z.array(ArchiveListItemSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});
export type ArchiveListResponse = z.infer<typeof ArchiveListResponseSchema>;

export const ArchiveFilterOptionsResponseSchema = z.object({
  sectors: z.array(z.string()),
  years: z.array(z.number().int()),
  // ClientPicker işi (2026-10-07): `clients` ad sırasıyla İLK 50 kayıt (öneri listesi);
  // `clientsTotal` tamamlanmış projesi olan toplam müşteri sayısı — fazlası arşiv
  // ekranındaki müşteri picker'ının sunucu aramasıyla (`GET /clients?search=`) bulunur.
  clients: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  clientsTotal: z.number().int().nonnegative().optional(),
});
export type ArchiveFilterOptionsResponse = z.infer<typeof ArchiveFilterOptionsResponseSchema>;

export const ArchiveDetailSchema = z.object({
  id: z.string().uuid(),
  clientName: z.string(),
  clientSector: z.string().nullable(),
  clientSize: z.string().nullable(),
  frameworkName: z.string(),
  frameworkVersion: z.string(),
  reportingYear: z.number().int(),
  periodType: PeriodType,
  assignedToName: z.string(),
  completedAt: z.string(),
  createdAt: z.string(),
  snapshot: z
    .object({
      sections: z.unknown(),
      questions: z.unknown(),
    })
    .nullable(),
});
export type ArchiveDetail = z.infer<typeof ArchiveDetailSchema>;
